import {defineStore} from 'pinia';
import {usePage} from '@inertiajs/vue3';
import {nextTick} from "vue";
import {numberCommaFormat} from "@js/helpers.ts";
import {checkDocument} from "@js/helpers.ts";

// Соответствия сегмента URL и ID категории
const categoryList = {
    'plitka': 1,
    'keramogranit': 2,
    'mozaika': 3,
    'stupeni': 4,
    'iskustvenniy_kamen': 5,
    'klinkernaya_plitka': 6,
}

// Типы сортировки
const orderTypes = {
    popularity: {
        type: 'popularity',
        title: 'По популярности',
    },
    desc: {
        type: 'asc',
        title: 'По дешевле',
    },
    asc: {
        type: 'desc',
        title: 'Сначала дороже',
    },
}

// Варианты количества товаров на странице
const perPageArray = [15, 30, 50, 100]

// Начальные значения всех фильтров (по-умолчанию)
const defaultFilterData = {
    colors: [],
    surfaces: [],
    brands: [],
    sizes: [],
    rooms: [],
    countries: [],
    patterns: [],
    is_rectification: [],
    is_promotional: [],
    is_mixed_pattern: [],
    forms: [],
    length: [0, 0],
    width: [0, 0],
    depth: [0, 0],
    price: [0, 0],
    measurement_units: [],
    resistances: [],
    anti_slips: [],
    chip_types: [],
    chip_height: [0, 0],
    chip_width: [0, 0],
    is_chip_mix_sized: [],
    fastenings: [],
    terrains: [],
    materials: [],
    purposes: [],
    per_page: 30,
    sort: orderTypes.popularity.type,
}

// Функция для вычисления шага слайдера между min и max
function calculateStep(min, max) {
    const diff = max - min;
    // если разница целая
    if (Number.isInteger(diff)) {
        return 1;
    }
    // иначе ищем максимальное количество знаков после точки
    const decimals = Math.max(
        (min.toString().split('.')[1]?.length) || 0,
        (max.toString().split('.')[1]?.length) || 0
    );
    // шаг = 1 / 10^decimals
    return 1 / Math.pow(10, decimals);
}

export const useCatalogStore = defineStore('catalogStore', {
    state: () => ({
        catalog_type: 'tile', //tile | collection
        category_id: 0,
        is_filter_open: false,
        loadings: {
            filter_first_load: false,
            catalog_loaded: false,
            filter_limits_loaded: false,
        },
        is_filter_watcher_on: false,
        total_result: 0,
        order_types: orderTypes,
        per_page_array: perPageArray,
        filter_data: {
            //Данные, которые задают лимиты фильтру, например min и max для слайдеров
            limits: {
                length: [0, 0],
                width: [0, 0],
                depth: [0, 0],
                price: [0, 0],
                chip_height: [0, 0],
                chip_width: [0, 0],
            },
        },
        filter: JSON.parse(JSON.stringify(defaultFilterData)),
        pagination: {
            current_page: 1,
            total_pages: 1,
        },
        timeout_events: {
            filter: undefined,
            pagination: undefined,
        },
        products: [],
        collections: [],
        selected_filters: [],
        search_query: null,
        savedScroll: 0,
        last_seen: null,
    }),
    actions: {
        // Переключает видимость панели фильтра
        toggleFilter() {
            this.is_filter_open = !this.is_filter_open
        },

        // При смене страницы через пагинацию
        onPageChange(page) {
            this.pagination.current_page = page
            this.getCatalog()                 // Загружаем новую страницу
            this.addParamsToUrl()             // Синхронизируем URL-параметры
            if (checkDocument) {
                window.scrollTo({top: 0, behavior: 'smooth'}) // Скролл наверх
            }
        },
        setSavedScroll(y) {
            this.savedScroll = y;
        },

        // Меняет тип сортировки
        changeOrder(type) {
            this.filter.sort = type
        },

        // Меняет количество на странице
        changePerPage(per_page) {
            this.filter.per_page = per_page
        },

        // Проверяет, есть ли в URL какие-либо параметры фильтра/страницы
        hasUrlParams() {
            const page = usePage();
            const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
            return queryParams.toString().length > 0;
        },

        // Синхронизирует параметры из URL в состояние фильтра
        syncUrlParamsWithState() {
            if (!this.hasUrlParams()) return;

            const page = usePage();
            const queryParams = new URLSearchParams(page.url.split('?')[1] || '');

            const getArrParam = (param) => (
                queryParams.has(param) && queryParams.get(param).length
                    ? JSON.parse(queryParams.get(param))
                    : defaultFilterData[param]
            );
            //Определяем каталог
            this.catalog_type = queryParams.has('catalog_type') ? queryParams.get('catalog_type') : 'tile';

            // Массивные параметры
            this.filter.colors = getArrParam('colors');
            this.filter.surfaces = getArrParam('surfaces');
            this.filter.brands = getArrParam('brands');
            this.filter.purposes = getArrParam('purposes');
            this.filter.sizes = getArrParam('sizes');
            this.filter.rooms = getArrParam('rooms');
            this.filter.countries = getArrParam('countries');
            this.filter.patterns = getArrParam('patterns');
            this.filter.forms = getArrParam('forms');
            this.filter.resistances = getArrParam('resistances');
            this.filter.anti_slips = getArrParam('anti_slips');
            this.filter.is_rectification = getArrParam('is_rectification');
            this.filter.is_promotional = getArrParam('is_promotional');
            this.filter.is_mixed_pattern = getArrParam('is_mixed_pattern');
            this.filter.measurement_units = getArrParam('measurement_units');
            this.filter.chip_types = getArrParam('chip_types');
            this.filter.is_chip_mix_sized = getArrParam('is_chip_mix_sized');
            this.filter.fastenings = getArrParam('fastenings');
            this.filter.terrains = getArrParam('terrains');
            this.filter.materials = getArrParam('materials');

            // Страница и сортировка
            // попытка взять из query
            const gerPerPageFromParam = queryParams.has('per_page')
                ? Number(queryParams.get('per_page'))
                : defaultFilterData.per_page;

            // если per_page есть и валидно — используем, иначе дефолт
            this.filter.per_page = perPageArray.includes(gerPerPageFromParam)
                ? gerPerPageFromParam
                : defaultFilterData.per_page

            this.filter.sort = queryParams.has('sort')
                ? queryParams.get('sort')
                : orderTypes.popularity.type;
            this.search_query = queryParams.has('search')
                ? queryParams.get('search')
                : null;
            this.pagination.current_page = queryParams.has('page')
                ? parseInt(queryParams.get('page'))
                : 1;
        },

        // Добавляет текущие фильтры и страницу в строку URL без перезагрузки
        addParamsToUrl() {
            if (!history.replaceState && checkDocument()) return;

            let queryString = new URLSearchParams({
                catalog_type: this.catalog_type,
                colors: JSON.stringify(this.filter.colors),
                surfaces: JSON.stringify(this.filter.surfaces),
                brands: JSON.stringify(this.filter.brands),
                sizes: JSON.stringify(this.filter.sizes),
                rooms: JSON.stringify(this.filter.rooms),
                countries: JSON.stringify(this.filter.countries),
                patterns: JSON.stringify(this.filter.patterns),
                is_rectification: this.filter.is_rectification,
                is_promotional: this.filter.is_promotional,
                is_mixed_pattern: this.filter.is_mixed_pattern,
                forms: JSON.stringify(this.filter.forms),
                length_from: this.filter.length[0],
                length_to: this.filter.length[1],
                width_from: this.filter.width[0],
                width_to: this.filter.width[1],
                depth_from: this.filter.depth[0],
                depth_to: this.filter.depth[1],
                price_from: this.filter.price[0],
                price_to: this.filter.price[1],
                measurement_units: JSON.stringify(this.filter.measurement_units),
                resistances: JSON.stringify(this.filter.resistances),
                anti_slips: JSON.stringify(this.filter.anti_slips),
                chip_types: JSON.stringify(this.filter.chip_types),
                chip_height_from: this.filter.chip_height[0],
                chip_height_to: this.filter.chip_height[1],
                chip_width_from: this.filter.chip_width[0],
                chip_width_to: this.filter.chip_width[1],
                is_chip_mix_sized: this.filter.is_chip_mix_sized,
                fastenings: JSON.stringify(this.filter.fastenings),
                terrains: JSON.stringify(this.filter.terrains),
                materials: JSON.stringify(this.filter.materials),
                purposes: JSON.stringify(this.filter.purposes),
                sort: this.filter.sort,
                page: this.pagination.current_page,
                per_page: this.filter.per_page,
            });

            if (this.search_query != null && this.search_query !== '') {
                queryString.set('search', this.search_query);
            }

            queryString = queryString.toString()
            const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${queryString}`;
            window.history.replaceState({path: newurl}, '', newurl);
        },

        // Получаем числовой параметр из URL, если нет — возвращаем defaultValue
        getParamFromUrl(param, defaultValue) {
            const page = usePage();
            const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
            return queryParams.has(param)
                ? parseFloat(queryParams.get(param))
                : defaultValue;
        },

        // Устанавливаем category_id, исходя из последнего сегмента пути
        setCategoryIdFromUrl() {
            const page = usePage();
            const urlWithoutQuery = page.url.split('?')[0];
            const pathSegments = urlWithoutQuery.split('/').filter(Boolean);
            const lastSegment = pathSegments[pathSegments.length - 1];

            if (lastSegment && categoryList[lastSegment]) {
                this.category_id = categoryList[lastSegment];
            } else {
                this.category_id = 0;
            }
        },
        //Делаем проверку для того, чтобы не передавать данные пока значение соответствует максимальному или минимальному лимиту
        checkRangeBeforeSend(type, value, checkType) {
            const checkTypeIndex = checkType === 'max' ? 1 : 0
            if (this.filter_data.limits[type][checkTypeIndex] === undefined) return null

            return this.filter_data.limits[type][checkTypeIndex] !== this.filter[type][checkTypeIndex] ? this.filter[type][checkTypeIndex] : null
        },
        // Запрашиваем данные для фильтра (списки и лимиты)
        async getFilterData() {
            this.loadings.catalog_loaded = false;
            this.is_filter_watcher_on = false;
            this.setCategoryIdFromUrl();

            try {
                const response = await axios.get('/axios/' + this.catalog_type + '/get-filter-data', {
                    params: {
                        category_id: this.category_id,
                        colors: this.filter.colors,
                        surfaces: this.filter.surfaces,
                        brands: this.filter.brands,
                        purposes: this.filter.purposes,
                        sizes: this.filter.sizes,
                        countries: this.filter.countries,
                        patterns: this.filter.patterns,
                        forms: this.filter.forms,
                        resistances: this.filter.resistances,
                        anti_slips: this.filter.anti_slips,
                        chip_types: this.filter.chip_types,
                        fastenings: this.filter.fastenings,
                        terrains: this.filter.terrains,
                        materials: this.filter.materials,
                        is_rectification: this.filter.is_rectification,
                        is_promotional: this.filter.is_promotional,
                        is_mixed_pattern: this.filter.is_mixed_pattern,
                        is_chip_mix_sized: this.category_id === 3 ? this.filter.is_chip_mix_sized : null,
                        price_from: this.checkRangeBeforeSend('price', this.filter_data.limits.price[0], 'min'),
                        price_to: this.checkRangeBeforeSend('price', this.filter_data.limits.price[1], 'max'),
                        width_from: this.checkRangeBeforeSend('width', this.filter_data.limits.width[0], 'min'),
                        width_to: this.checkRangeBeforeSend('width', this.filter_data.limits.width[1], 'max'),
                        length_from: this.checkRangeBeforeSend('length', this.filter_data.limits.length[0], 'min'),
                        length_to: this.checkRangeBeforeSend('length', this.filter_data.limits.length[1], 'max'),
                        chip_height_from: this.category_id === 3 ? this.checkRangeBeforeSend('chip_height', this.filter_data.limits.chip_height[0], 'min') : null,
                        chip_height_to: this.category_id === 3 ? this.checkRangeBeforeSend('chip_height', this.filter_data.limits.chip_height[1], 'max') : null,
                        chip_width_from: this.category_id === 3 ? this.checkRangeBeforeSend('chip_width', this.filter_data.limits.chip_width[0], 'min') : null,
                        chip_width_to: this.category_id === 3 ? this.checkRangeBeforeSend('chip_width', this.filter_data.limits.chip_width[1], 'max') : null,
                        search: this.search_query,
                    }
                });

                const limits = response.data.limits;

                // Если лимиты ещё не были загружены, устанавливаем начальные значения
                if (!this.loadings.filter_limits_loaded) {
                    // Ширина
                    this.filter_data.limits.width[0] = limits.min_width ?? 0;
                    this.filter_data.limits.width[1] = limits.max_width ?? 0;
                    this.filter.width[0] = this.getParamFromUrl('width_from', limits.min_width ?? 0);
                    this.filter.width[1] = this.getParamFromUrl('width_to', limits.max_width ?? 0);

                    // Длина
                    this.filter_data.limits.length[0] = limits.min_length ?? 0;
                    this.filter_data.limits.length[1] = limits.max_length ?? 0;
                    this.filter.length[0] = this.getParamFromUrl('length_from', limits.min_length ?? 0);
                    this.filter.length[1] = this.getParamFromUrl('length_to', limits.max_length ?? 0);

                    // Цена
                    this.filter_data.limits.price[0] = limits.min_price ?? 0;
                    this.filter_data.limits.price[1] = limits.max_price ?? 0;
                    this.filter.price[0] = this.getParamFromUrl('price_from', limits.min_price ?? 0);
                    this.filter.price[1] = this.getParamFromUrl('price_to', limits.max_price ?? 0);

                    // Толщина
                    this.filter_data.limits.depth[0] = limits.min_depth ?? 0;
                    this.filter_data.limits.depth[1] = limits.max_depth ?? 0;
                    this.filter.depth[0] = this.getParamFromUrl('depth_from', limits.min_depth ?? 0);
                    this.filter.depth[1] = this.getParamFromUrl('depth_to', limits.max_depth ?? 0);

                    // Высота фишки
                    this.filter_data.limits.chip_height[0] = limits.min_chip_height ?? 0;
                    this.filter_data.limits.chip_height[1] = limits.max_chip_height ?? 0;
                    this.filter.chip_height[0] = this.getParamFromUrl('chip_height_from', limits.min_chip_height ?? 0);
                    this.filter.chip_height[1] = this.getParamFromUrl('chip_height_to', limits.max_chip_height ?? 0);

                    // Ширина фишки
                    this.filter_data.limits.chip_width[0] = limits.min_chip_width ?? 0;
                    this.filter_data.limits.chip_width[1] = limits.max_chip_width ?? 0;
                    this.filter.chip_width[0] = this.getParamFromUrl('chip_width_from', limits.min_chip_width ?? 0);
                    this.filter.chip_width[1] = this.getParamFromUrl('chip_width_to', limits.max_chip_width ?? 0);
                }

                // Устанавливаем списки опций из ответа сервера
                const filter_data = response.data.filter_data;
                this.filter_data.colors = filter_data.colors ?? [];
                this.filter_data.surfaces = filter_data.surfaces ?? [];
                this.filter_data.brands = filter_data.brands ?? [];
                this.filter_data.purposes = filter_data.purposes ?? [];
                this.filter_data.sizes = filter_data.sizes ?? [];
                this.filter_data.rooms = filter_data.rooms ?? [];
                this.filter_data.countries = filter_data.countries ?? [];
                this.filter_data.patterns = filter_data.patterns ?? [];
                this.filter_data.forms = filter_data.forms ?? [];
                this.filter_data.resistances = filter_data.resistances ?? [];
                this.filter_data.anti_slips = filter_data.anti_slips ?? [];
                this.filter_data.chip_types = filter_data.chip_types ?? [];
                this.filter_data.fastenings = filter_data.fastenings ?? [];
                this.filter_data.terrains = filter_data.terrains ?? [];
                this.filter_data.materials = filter_data.materials ?? [];
                this.filter_data.is_rectification = filter_data.is_rectification ?? [];
                this.filter_data.is_promotional = filter_data.is_promotional ?? [];
                this.filter_data.is_mixed_pattern = filter_data.is_mixed_pattern ?? [];
                this.filter_data.is_chip_mix_sized = filter_data.is_chip_mix_sized ?? [];

                this.addParamsToUrl();

                // Обновляем флаги загрузки и активируем watcher
                this.loadings.filter_limits_loaded = true;
                this.loadings.filter_first_load = true;
                // nextTick - Ждём, пока Vue применит все реактивные изменения и обновит DOM,
                // чтобы включить watcher уже на окончательный стейт, а не на промежуточные данные
                await nextTick();
                this.is_filter_watcher_on = true;

                // Обновляем список выбранных фильтров и загружаем товары
                this.updateSelectedFilters();
                this.getCatalog();
            } catch (error) {
                console.error(error.message);
                this.loadings.filter_limits_loaded = false;
                this.loadings.catalog_loaded = false;
            }
        },

        // Запрашиваем товары с учётом фильтра и пагинации
        getCatalog() {
            axios.get('/axios/' + this.catalog_type + '/catalog', {
                params: {
                    category_id: this.category_id,
                    colors: this.filter.colors,
                    surfaces: this.filter.surfaces,
                    brands: this.filter.brands,
                    purposes: this.filter.purposes,
                    sizes: this.filter.sizes,
                    countries: this.filter.countries,
                    patterns: this.filter.patterns,
                    forms: this.filter.forms,
                    resistances: this.filter.resistances,
                    anti_slips: this.filter.anti_slips,
                    chip_types: this.filter.chip_types,
                    fastenings: this.filter.fastenings,
                    terrains: this.filter.terrains,
                    materials: this.filter.materials,
                    is_rectification: this.filter.is_rectification,
                    is_promotional: this.filter.is_promotional,
                    is_mixed_pattern: this.filter.is_mixed_pattern,
                    is_chip_mix_sized: this.category_id === 3 ? this.filter.is_chip_mix_sized : null,
                    price_from: this.checkRangeBeforeSend('price', this.filter_data.limits.price[0], 'min'),
                    price_to: this.checkRangeBeforeSend('price', this.filter_data.limits.price[1], 'max'),
                    width_from: this.checkRangeBeforeSend('width', this.filter_data.limits.width[0], 'min'),
                    width_to: this.checkRangeBeforeSend('width', this.filter_data.limits.width[1], 'max'),
                    length_from: this.checkRangeBeforeSend('length', this.filter_data.limits.length[0], 'min'),
                    length_to: this.checkRangeBeforeSend('length', this.filter_data.limits.length[1], 'max'),
                    chip_height_from: this.category_id === 3 ? this.checkRangeBeforeSend('chip_height', this.filter_data.limits.chip_height[0], 'min') : null,
                    chip_height_to: this.category_id === 3 ? this.checkRangeBeforeSend('chip_height', this.filter_data.limits.chip_height[1], 'max') : null,
                    chip_width_from: this.category_id === 3 ? this.checkRangeBeforeSend('chip_width', this.filter_data.limits.chip_width[0], 'min') : null,
                    chip_width_to: this.category_id === 3 ? this.checkRangeBeforeSend('chip_width', this.filter_data.limits.chip_width[1], 'max') : null,
                    sort: this.filter.sort,
                    page: this.pagination.current_page,
                    per_page: this.filter.per_page,
                    search: this.search_query,
                    last_seen: this.last_seen
                }
            }).then(response => {
                // Обновляем данные пагинации и товаров
                this.pagination.total_pages = response.data.total_pages ?? 1;
                this.total_result = response.data.total ?? 0;
                this.products = response.data.products ?? [];
                this.collections = response.data.collections ?? [];
                this.loadings.catalog_loaded = true;
                this.last_seen = response.data.last_seen;
            });
        },

        // Собираем массив выбранных фильтров для отображения "чипов"
        updateSelectedFilters() {
            const selected = [];

            // Вспомогательная функция для списковых опций
            const pushSelected = (field, optionsList, label) => {
                if (this.filter[field]?.length) {
                    this.filter[field].forEach(id => {
                        const found = optionsList.find(item => item.id == id);
                        if (found) {
                            selected.push({
                                label,
                                title: found.title,
                                value: id,
                                field,
                            });
                        }
                    });
                }
            };

            // Обрабатываем все простые списковые фильтры
            pushSelected('colors', this.filter_data.colors, 'Цвет');
            pushSelected('surfaces', this.filter_data.surfaces, 'Поверхность');
            pushSelected('brands', this.filter_data.brands, 'Бренд');
            pushSelected('purposes', this.filter_data.purposes, 'Назначение');
            pushSelected('sizes', this.filter_data.sizes, 'Размер');
            pushSelected('rooms', this.filter_data.rooms, 'Помещение');
            pushSelected('countries', this.filter_data.countries, 'Страна');
            pushSelected('patterns', this.filter_data.patterns, 'Узор');
            pushSelected('forms', this.filter_data.forms, 'Форма');
            pushSelected('resistances', this.filter_data.resistances, 'Стойкость');
            pushSelected('anti_slips', this.filter_data.anti_slips, 'Противоскольжение');
            pushSelected('chip_types', this.filter_data.chip_types, 'Тип фишки');
            pushSelected('fastenings', this.filter_data.fastenings, 'Крепление');
            pushSelected('terrains', this.filter_data.terrains, 'Рельеф');
            pushSelected('materials', this.filter_data.materials, 'Материал');

            // Вспомогательная функция для булевых (чекбокс) фильтров
            const pushCheckbox = (field, label) => {
                if (this.filter[field]?.length) {
                    selected.push({
                        label,
                        title: 'Да',
                        value: 1,
                        field,
                    });
                }
            };

            pushCheckbox('is_rectification', 'Ректификация');
            pushCheckbox('is_promotional', 'Акция');
            pushCheckbox('is_mixed_pattern', 'Микс рисунков');
            pushCheckbox('is_chip_mix_sized', 'Фишки разного размера');

            // Вспомогательная функция для диапазонов (слайдеров)
            const pushRange = (field, label, unit = 'мм') => {
                const [min, max] = this.filter[field];
                const [limitMin, limitMax] = this.filter_data.limits[field] || [0, 0];
                // Добавляем, только если диапазон отличается от полного лимита
                if (min > limitMin || max < limitMax) {
                    const formatedMin = numberCommaFormat(min);
                    const formatedMax = numberCommaFormat(max);
                    selected.push({
                        label,
                        title: `${formatedMin} - ${formatedMax} ${unit}`,
                        value: `${min}_${max}`,
                        field,
                    });
                }
            };

            pushRange('price', 'Цена', '₽');
            pushRange('length', 'Длина');
            pushRange('width', 'Ширина');
            pushRange('depth', 'Толщина');
            pushRange('chip_height', 'Высота фишки');
            pushRange('chip_width', 'Ширина фишки');

            this.selected_filters = selected;
        },

        // Полный сброс фильтра до дефолтных значений
        resetFilter() {
            this.loadings.catalog_loaded = false;
            this.loadings.filter_first_load = false;
            this.loadings.filter_limits_loaded = false;

            this.pagination.current_page = 1;

            this.products = [];
            this.collections = [];
            this.filter_data = {
                limits: {
                    length: [0, 0],
                    width: [0, 0],
                    depth: [0, 0],
                    price: [0, 0],
                    chip_height: [0, 0],
                    chip_width: [0, 0],
                },
            };

            this.filter = JSON.parse(JSON.stringify(defaultFilterData));
            this.last_seen = null
        },

        // Удаление одного фильтра по клику на "чип"
        removeFilter(tag) {
            if (!tag?.field) return;
            const rangeFields = ['price', 'length', 'width', 'depth', 'chip_height', 'chip_width'];

            if (rangeFields.includes(tag.field)) {
                // Диапазон: возвращаем к лимитам
                if (this.filter_data.limits[tag.field]) {
                    const [limitMin, limitMax] = this.filter_data.limits[tag.field].map(Number);
                    this.filter[tag.field] = [limitMin, limitMax];
                } else {
                    // На всякий случай если лимитов нет — сбрасываем в [0,0]
                    this.filter[tag.field] = [0, 0];
                }
            } else if (Array.isArray(this.filter[tag.field])) {
                // Убираем из массива
                this.filter[tag.field] = this.filter[tag.field].filter(v => v != tag.value);
            } else {
                // На всякий случай сбрасываем
                this.filter[tag.field] = [];
            }

            this.setCategoryIdFromUrl()
            this.updateSelectedFilters();

            this.last_seen = null
        }
    },

    // Геттеры для вычисления шага каждого слайдера
    getters: {
        lengthInterval(state) {
            const [min, max] = state.filter_data.limits.length;
            return calculateStep(min, max);
        },
        widthInterval(state) {
            const [min, max] = state.filter_data.limits.width;
            return calculateStep(min, max);
        },
        depthInterval(state) {
            const [min, max] = state.filter_data.limits.depth;
            return calculateStep(min, max);
        },
        priceInterval(state) {
            const [min, max] = state.filter_data.limits.price;
            return calculateStep(min, max);
        },
        chipHeightInterval(state) {
            const [min, max] = state.filter_data.limits.chip_height;
            return calculateStep(min, max);
        },
        chipWidthInterval(state) {
            const [min, max] = state.filter_data.limits.chip_width;
            return calculateStep(min, max);
        },
    },
});
